/**
 * Вход в партию: выбор комнаты, взгляд на стол и своя фишка.
 *
 * Два обязательных шага. Первый — комната: живой список и своё название. Второй —
 * стол: кто сидит, моя фишка из прошлого раза, переключатель роли и одна кнопка, надпись
 * которой повторяет роль: «Play» или «View».
 * Фишку правят в отдельном окне поверх шага (имя, аватарка, цвет); тем, кто
 * приходит не в первый раз, оно не нужно вовсе: комната, «Play» — и ты за столом.
 *
 * Визард показывает по шагу на экран. Правил игры здесь нет: экран собирает выбор
 * и отправляет одно действие, остальное решает сервер.
 */

import { useEffect, useRef, useState, type FormEvent, type RefObject } from 'react'
import { flushSync } from 'react-dom'
import { bridge, store, stored, useBridge } from './app.socket.js'
import { PLAYER_AVATARS } from './blocks/avatars.js'
import { markerColor } from './blocks/pawn.markup.js'
import {
  FreeSeat,
  SeatPlayer,
  seatHeight,
  useSeatSize,
  ConnectionNote,
  BUTTON,
  BUTTON_OUTLINE,
  LeaveGameButton,
  CARD,
  CARD_TITLE,
  dimWhen,
  lockedWhen,
  Shell,
} from './app.dialogs.js'
import { MAX_ROOMS, NAME_MAX, PAWNS, ROOM_NAME_MAX, type PawnId, type RoomSeat } from '../srv/protocol.js'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
// Набор 16: тот же рисунок, но штрих толще относительно кадра.
import { CheckIcon, EyeIcon, MapPinIcon, PencilIcon } from '@heroicons/react/16/solid'
// Замок контурный: галка тоже штрих, и пара читается одним набором.
import { LockClosedIcon } from '@heroicons/react/24/outline'

/*
 * Что переживает перезагрузку (localStorage): имя, аватарка и цвет — выбирать их
 * заново при каждом входе утомительно — и раскладка входа. Это пожелания, а не
 * факты: комната может оказаться с уже занятым цветом, и тогда за столом человек
 * получит свободный, а сохранённое не тронется, пока он сам ничего не поменяет.
 */
const NAME_KEY = 'tolk.name'
const AVATAR_KEY = 'tolk.avatar'
const PAWN_KEY = 'tolk.pawn'

const loadName = (): string => stored(NAME_KEY) ?? 'Player'

/** Незнакомое имя откатываем на первое из набора: набор рисовки мог смениться между заходами. */
const loadAvatar = (): string => {
  const saved = stored(AVATAR_KEY) ?? ''
  return (PLAYER_AVATARS as readonly string[]).includes(saved) ? saved : PLAYER_AVATARS[0]
}

/** То же для цвета: палитра менялась и ещё может сменяться. */
const loadPawn = (): PawnId => {
  const saved = stored(PAWN_KEY) ?? ''
  return (PAWNS.some((p) => p.id === saved) ? saved : PAWNS[0].id) as PawnId
}

/**
 * Минимальная ширина карточки шага. Открытая панель отладки съедает до трёхсот
 * пикселей, и без нижней границы карточка схлопывалась до слов в столбик. Узкая
 * область теперь прокручивается, а не сжимает: 17rem — столько нужно строке
 * кнопок и ряду мест, чтобы остаться читаемыми. Центрирует `m-auto`, а не
 * `justify-center`: при переполнении `m-auto` не отрезает левый край.
 */
const MIN_CARD = 'min-w-[17rem]'

/** Комната из списка или новая: у новой кода ещё нет. */
type RoomChoice = { code: string | null; roomName: string; started: boolean }

/**
 * Пометка выбора в рядах аватарки и цвета: бейдж с галкой на кромке кружка.
 *
 * Одна система на оба ряда. Галку не кладут поверх картинки — поверх лица её
 * пришлось бы сажать на затемнение всей плитки, а ряд существует ровно затем,
 * чтобы лицо видеть; поэтому она в углу, как у Google, Teams и Zoom.
 *
 * Бейдж залит светлой бирюзой — та же лестница, что у кольца фокуса и подсветки
 * полей, только выше ступенью; галка внутри тёмная, 9.41:1. Наоборот было бы
 * 2.35:1, ниже порога 3:1 для нетекстовых элементов.
 *
 * Кромка 2px — цветом самой карточки, поэтому бейдж выглядит вырезанным из формы
 * и отделяется от образца любого цвета. Цвет не числом, а тем же выражением,
 * каким залита карточка: иначе палитра форкнулась бы в разметку (R106).
 */
const BADGE =
  'pointer-events-none absolute -right-0.5 -bottom-0.5 size-4 items-center justify-center rounded-full text-deep shadow-[0_0_0_2px_color-mix(in_srgb,var(--color-surface)_80%,var(--color-deep))]'
const CHECK = `${BADGE} hidden bg-indigo-300 peer-checked:flex`
/**
 * Замок занятого: тот же бейдж и того же размера, что галка, но серый — не выбор,
 * а запрет. Заливка той же яркости, что у галки, чтобы круги читались одного
 * размера (он один, 16).
 */
const LOCK = `${BADGE} flex bg-gray-300`

function ChoiceBadge({ taken }: { taken: boolean }) {
  return (
    <span className={taken ? LOCK : CHECK}>
      {taken ? (
        <LockClosedIcon aria-hidden="true" strokeWidth={2.5} className="size-3" />
      ) : (
        <CheckIcon aria-hidden="true" className="size-3.5" />
      )}
    </span>
  )
}

/**
 * Что человек получит за столом: его пожелание, а если цвет или аватарка в этой
 * комнате заняты — первые свободные (R117). Считается из состава комнаты каждый
 * раз заново, поэтому, пока человек смотрит, выбор сам уходит от занятого.
 */
function resolveLook(pawn: PawnId, avatar: string, seats: readonly RoomSeat[]) {
  const takenPawns = new Set<string>(seats.map((s) => s.pawn))
  const takenAvatars = new Set<string>(seats.map((s) => s.avatar).filter(Boolean))
  const freePawn = PAWNS.find((p) => !takenPawns.has(p.id))
  const freeAvatar = PLAYER_AVATARS.find((a) => !takenAvatars.has(a))
  const effPawn = (takenPawns.has(pawn) && freePawn ? freePawn.id : pawn) as PawnId
  const effAvatar = takenAvatars.has(avatar) && freeAvatar ? freeAvatar : avatar
  return {
    pawn: effPawn,
    avatar: effAvatar,
    takenPawns,
    takenAvatars,
  }
}

/** Отказ сервера человеческими словами: сервер говорит по-русски и не для этого экрана. */
function describeError(code: string | null, reason: string | null): string | null {
  switch (code) {
    case null:
      return reason
    case 'pawn_taken':
      return 'That colour was just taken. Check your player and try again.'
    case 'avatar_taken':
      return 'That avatar was just taken. Check your player and try again.'
    case 'name_taken':
      return 'That name is taken. Pick another.'
    case 'game_full':
      return 'The room is full.'
    case 'already_started':
      return 'The game has already started.'
    case 'game_not_found':
      return 'That room is gone.'
    case 'rooms_full':
      return `All ${MAX_ROOMS} rooms are taken. Join one of them.`
    case 'invalid_payload':
      return `Use a name of 1 to ${NAME_MAX} characters.`
    default:
      return reason
  }
}

/**
 * «Назад» в подвале шага: контурная кнопка слева от главной. Рамка, а не заливка —
 * главное действие шага должно оставаться единственным цветным пятном.
 */
function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={`${BUTTON_OUTLINE} max-[26rem]:px-3`}>
      <ChevronLeftIcon aria-hidden="true" className="-ml-1.5 size-5 max-[26rem]:ml-0" />
      {/* На телефоне три кнопки в ряд не помещаются: «Back» остаётся стрелкой, подпись — для диктора. */}
      <span className="max-[26rem]:sr-only">Back</span>
    </button>
  )
}

/** Поле ввода: подсказка внутри, подпись только для экранного диктора. */
const FIELD =
  'block w-full rounded-full bg-white/5 px-4 py-2 text-base text-white outline-1 -outline-offset-1 outline-white/10 placeholder:text-gray-500 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500 sm:text-sm/6'

/**
 * Форма фишки: имя, аватарка, цвет. Живёт окном поверх шага.
 *
 * Занятое в комнате отмечено и недоступно: и аватарка, и цвет остаются как есть
 * и несут один и тот же бейдж с замком, того же размера, что галка выбора.
 * Они остаются узнаваемыми — игрок видит, чем занято место, а не пустой кружок.
 */
function LookForm({
  name,
  onName,
  nameError,
  pawn,
  avatar,
  takenPawns,
  takenAvatars,
  onPawn,
  onAvatar,
  nameRef,
  onEnter,
}: {
  name: string
  onName: (value: string) => void
  nameError: string | null
  pawn: PawnId
  avatar: string
  takenPawns: ReadonlySet<string>
  takenAvatars: ReadonlySet<string>
  onPawn: (pawn: PawnId) => void
  onAvatar: (avatar: string) => void
  nameRef?: RefObject<HTMLInputElement>
  /** Enter в имени, на аватарке или цвете: форма «отправлена». Нет обработчика — Enter ничего не делает. */
  onEnter?: () => void
}) {
  return (
    <div
      onKeyDown={(e) => {
        // Это не `<form>`: радио в нём сами Enter не отправляют. Ловим на корне, чтобы работало на любом поле.
        if (e.key !== 'Enter' || e.nativeEvent.isComposing || !onEnter) return
        e.preventDefault()
        onEnter()
      }}
    >
      {/* Фишка, как она встанет за столом: тот же компонент, что в игре, — показанное и нарисованное не разойдутся. */}
      <div className="flex justify-center">
        <SeatPlayer avatar={avatar} pawn={pawn} name={name.trim() || 'YOU'} />
      </div>

      <div className="mt-6">
        <label htmlFor="name" className="sr-only">
          Name
        </label>
        <div className="relative">
          <input
            id="name"
            ref={nameRef}
            value={name}
            maxLength={NAME_MAX}
            placeholder="Enter your name"
            onChange={(e) => onName(e.target.value)}
            aria-invalid={!!nameError}
            className={`pr-14 ${nameError ? `${FIELD} outline-red-500 focus:outline-red-500` : FIELD}`}
          />
          <span className="absolute inset-y-0 right-4 flex items-center text-[11px] text-gray-500 tabular-nums">
            {name.trim().length}/{NAME_MAX}
          </span>
        </div>
        {nameError && (
          <p role="alert" className="mt-2 text-sm text-red-500">
            {nameError}
          </p>
        )}
      </div>

      <fieldset className="mt-8">
        <legend className="sr-only">Avatar</legend>
        {/*
          Настоящие радио-кнопки: браузер сам даёт стрелки внутри группы, одну
          остановку табуляции на всю группу и кольцо фокуса. Ввод лежит
          прозрачным кружком поверх рисунка, а не спрятан, — нажатие попадает
          прямо в радио, и кольцо фокуса появляется сразу, как в ряду цветов.

          Выбор и фокус разведены по каналам: выбор — бейдж (`CHECK`), фокус —
          кольцо. Вешать оба на `outline` нельзя, он у элемента один.
        */}
        <div className="mt-2 grid w-full grid-cols-6 justify-items-center gap-3">
          {PLAYER_AVATARS.map((a) => {
            const taken = takenAvatars.has(a)
            return (
              <label
                key={a}
                className={`relative flex rounded-full ${taken ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <input
                  type="radio"
                  name="avatar"
                  aria-label={taken ? `${a} (taken)` : a}
                  value={a}
                  disabled={taken}
                  checked={a === avatar}
                  onChange={() => onAvatar(a)}
                  className="peer absolute inset-0 appearance-none rounded-full focus:outline-2 focus:outline-offset-2 focus:outline-indigo-500 enabled:cursor-pointer"
                />
                <span className="size-[2.8125rem] overflow-hidden rounded-full bg-deep/60 outline-1 -outline-offset-1 outline-white/10 peer-checked:bg-white/10">
                  <svg viewBox="0 0 300 300" className="size-full" aria-hidden="true">
                    <use href={`#avatar-0-${a}`} />
                  </svg>
                </span>
                <ChoiceBadge taken={taken} />
              </label>
            )
          })}
        </div>
      </fieldset>

      <fieldset className="mt-8">
        <legend className="sr-only">Color</legend>
        {/* Те же шесть колонок, что у аватарок: обе сетки на всю ширину, образец стоит по центру своей клетки. */}
        <div className="mt-2 grid w-full grid-cols-6 justify-items-center gap-3">
          {PAWNS.map((p) => {
            const taken = takenPawns.has(p.id)
            return (
              <div
                key={p.id}
                className="relative flex rounded-full outline -outline-offset-1 outline-white/10"
              >
                <input
                  type="radio"
                  name="pawn"
                  aria-label={taken ? `${p.id} (taken)` : p.id}
                  value={p.id}
                  disabled={taken}
                  checked={pawn === p.id}
                  onChange={() => onPawn(p.id as PawnId)}
                  style={{ backgroundColor: markerColor(p.id) }}
                  className={`peer size-[2.5875rem] appearance-none rounded-full forced-color-adjust-none focus:outline-2 focus:outline-offset-2 focus:outline-indigo-500 ${taken ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                />
                <ChoiceBadge taken={taken} />
              </div>
            )
          })}
        </div>
      </fieldset>
    </div>
  )
}

/*
 * Строка из микро-карточки и переключателя: оба 36 px высотой, и видимый внутренний
 * отступ у обоих один — 4 px. У переключателя это `p-1` вокруг выбранной таблетки
 * (28 px). У карточки кольцо аватарки (24 + 2·2 = 28 px) рисуется `box-shadow` и
 * выходит за её рамку на 2 px, поэтому слева стоит `pl-1.5` (6 − 2 = 4), а сверху и
 * снизу те же 4 дают сами 36 − 28.
 */

/**
 * Переключатель роли: «играть» и «смотреть». Радио-группа из двух значков с
 * подписью, а не выпадающий список и не пара кнопок действия: выбор влияет на
 * вид моего места в ряду, а входит всегда одна кнопка.
 */
function RoleSwitch({
  role,
  canPlay,
  canWatch,
  onChange,
}: {
  role: 'play' | 'watch'
  canPlay: boolean
  canWatch: boolean
  onChange: (mode: 'play' | 'watch') => void
}) {
  const options = [
    { id: 'play' as const, label: 'Play', icon: MapPinIcon, enabled: canPlay, why: 'The room is closed' },
    { id: 'watch' as const, label: 'View', icon: EyeIcon, enabled: canWatch, why: 'Nothing to view yet' },
  ]
  return (
    <div
      role="radiogroup"
      aria-label="Role"
      className="flex h-9 grow items-stretch rounded-full bg-white/5 p-1 outline-1 -outline-offset-1 outline-white/10"
    >
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={role === o.id}
          disabled={!o.enabled}
          title={o.enabled ? undefined : o.why}
          onClick={() => onChange(o.id)}
          className={`flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-full px-4 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400 disabled:cursor-not-allowed ${role === o.id ? 'bg-indigo-700 text-white' : o.enabled ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600'}`}
        >
          <o.icon aria-hidden="true" className="size-4" />
          {o.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Вход в партию.
 *
 * Состояние: что выбрано, кто за столом и что нужно человеку.
 *
 * `shift` — половина открытой боковой панели, как у сцены игры: карточки лобби
 * стоят по центру области справа от неё, и окно правки обязано стоять там же, а
 * не по центру всего окна.
 */
export function Lobby({ shift = 0 }: { shift?: number }) {
  const s = useBridge()
  const [name, setName] = useState(loadName)
  /** Пожелания: цвет и аватарка из прошлого раза. За столом их может заменить занятость. */
  const [pawn, setPawn] = useState<PawnId>(loadPawn)
  const [avatar, setAvatar] = useState(loadAvatar)
  /** Выбранная комната — по коду: счёт мест и статус берутся из живого списка. */
  const [pick, setPick] = useState<string | null>(null)
  /** Название новой комнаты, которой на сервере ещё нет: она появится при входе. */
  const [fresh, setFresh] = useState<string | null>(null)
  const [newRoomName, setNewRoomName] = useState('')
  /** Шаг визарда: 1 — комната, 2 — стол и я. */
  const [step, setStep] = useState<1 | 2>(1)
  /** Окно правки фишки открыто. */
  const [editing, setEditing] = useState(false)
  /** Кем входить: играть или смотреть. Не запоминается: возвращающийся игрок входит игроком. */
  const [mode, setMode] = useState<'play' | 'watch'>('play')
  /** Что случилось с выбором, пока человек смотрел в другую сторону. */
  const [notice, setNotice] = useState<string | null>(null)
  /** Отказ самой формы — до сервера не дошло. */
  const [nameError, setNameError] = useState<string | null>(null)
  /** Ошибка сервера, которую человек уже увидел и начал править. */
  const [dismissed, setDismissed] = useState<string | null>(null)
  /** Запрос на вход ушёл, ответа ещё нет: повторное нажатие завело бы второй слот. */
  const [pending, setPending] = useState(false)
  const nameInput = useRef<HTMLInputElement>(null)
  /** Место за столом, которое сохранено, пока человек в лобби: код комнаты или `null`. */
  const held = s.lobbyView ? s.code : null
  /** До первого списка лобби не показывают (см. `App`); `null` здесь — только отказ сети. */
  const rooms = s.rooms ?? []

  useEffect(() => store(NAME_KEY, name), [name])
  useEffect(() => store(AVATAR_KEY, avatar), [avatar])
  useEffect(() => store(PAWN_KEY, pawn), [pawn])

  /*
   * Выбор хранится кодом, а комната берётся из живого списка каждый раз заново:
   * счёт мест и статус обновляются сами, а исчезнувшая комната перестаёт быть
   * выбранной без отдельного сброса. Новая комната — только по явному `Create`.
   */
  const picked = rooms.find((r) => r.code === pick)
  /** Состав выбранной комнаты. У новой он пуст: за её столом ещё никого. */
  const seats = picked?.seats ?? []
  const viewers = picked?.viewers ?? 0
  const maxPlayers = picked?.maxPlayers ?? 4
  const target: RoomChoice | null =
    fresh !== null
      ? { code: null, roomName: fresh, started: false }
      : picked
        ? { code: picked.code, roomName: picked.roomName, started: picked.started }
        : null

  /*
   * Место за столом сохранено: в свою комнату можно только вернуться (зрителем или вторым игроком
   * в неё не войти), в чужую и в новую не войти, пока не вернулся или не вышел насовсем.
   */
  const sitting = held !== null
  const mine = sitting && target?.code === held

  /*
   * Мест нет: войти игроком нельзя, как и в начатую партию, — сервер откажет.
   * Говорим об этом сразу, а не отказом после нажатия.
   */
  const full = !!picked && !picked.started && picked.players >= picked.maxPlayers
  const locked = (target?.started ?? false) || full
  const lockNote = sitting
    ? mine
      ? 'You are seated at this table.'
      : 'You are seated at another table. Return to it or leave it first.'
    : target?.started
      ? picked?.finished
        ? 'Game finished. You can view the board.'
        : 'Game in progress. You can only view.'
      : full
        ? 'Room is full. You can only view.'
        : null
  /** Смотреть можно только то, что уже есть на сервере: у новой комнаты партии ещё нет. */
  const canWatch = !!target?.code && (picked?.players ?? 0) > 0
  /*
   * Роль, с которой войдём. Закрытая комната даёт только «смотреть», у новой
   * комнаты смотреть нечего. От роли зависит всё: вид моего места
   * (капля в ряду — за столом, метка с глазом под рядом — из зала) и действие единственной кнопки.
   */
  const role: 'play' | 'watch' = locked ? 'watch' : canWatch && mode === 'watch' ? 'watch' : 'play'
  /*
   * Ряд показывает всю вместимость комнаты. Моё место занимает один слот
   * только в роли игрока; зритель за столом не сидит.
   */
  const slots = Math.max(maxPlayers, seats.length)
  const { ref: seatsRef, size: seatW } = useSeatSize(slots)
  /** Шаг, который реально показан: без комнаты нечего подтверждать дальше первого. */
  const view: 1 | 2 = target ? step : 1

  /** Что человек получит за столом: пожелание, а при занятости — свободное. */
  const look = resolveLook(pawn, avatar, seats)
  const color = markerColor(look.pawn)

  /** Что показать про отказ: сначала отказ формы, потом свежий отказ сервера. */
  const serverError = s.lastError !== dismissed ? describeError(s.lastErrorCode, s.lastError) : null
  const error = nameError ?? serverError

  // --- вход ------------------------------------------------------------------

  const openEditor = () => setEditing(true)

  /** Закрыть окно правки. Пустое имя не отпускает: с ним войти нельзя, и лучше сказать здесь. */
  const closeEditor = () => {
    if (!name.trim()) {
      setNameError('Enter your name')
      nameInput.current?.focus()
      return
    }
    setEditing(false)
  }

  const join = () => {
    if (!target || pending || sitting) return
    if (!name.trim()) {
      setNameError('Enter your name')
      openEditor()
      return
    }
    if (!s.connected) return
    setPending(true)
    setDismissed(null)
    const base = {
      commandId: bridge.nextCommandId(),
      name: name.trim(),
      pawn: look.pawn,
      avatar: look.avatar,
    }
    const sent =
      target.code === null
        ? bridge.send({ kind: 'createGame', roomName: target.roomName, ...base })
        : bridge.send({ kind: 'joinGame', code: target.code, ...base })
    // Вход закончился ответом сервера: либо в партии, либо отказ, который уже в `lastError`.
    void sent.then(() => setPending(false))
  }

  const watchRoom = () => {
    if (target?.code && !sitting) bridge.watchGame(target.code)
  }

  // Имя занято: чинится в окне фишки, и нести человека туда должно само.
  useEffect(() => {
    if (s.lastErrorCode === 'name_taken') openEditor()
  }, [s.lastErrorCode, s.lastError]) // eslint-disable-line react-hooks/exhaustive-deps

  // Комнату закрыли, пока на неё смотрели: сервер вернул нас сюда и сказал почему.
  useEffect(() => {
    if (s.lastErrorCode === 'game_not_found') setNotice('That room is gone')
  }, [s.lastErrorCode, s.lastError])

  // Выбранная комната исчезла из списка (все вышли, срок вышел): сбрасываем и говорим.
  useEffect(() => {
    if (pick !== null && fresh === null && !picked) {
      setPick(null)
      setNotice('That room is gone')
    }
  }, [pick, fresh, picked])

  // --- шаги визарда и история -----------------------------------------------

  /*
   * Шаги визарда живут в истории браузера: «назад» на телефоне и в браузере
   * возвращает на прошлый шаг, а не выкидывает со страницы. Вперёд — pushState,
   * назад — своя кнопка зовёт `history.back()`, а не ставит шаг сама, чтобы
   * история и экран не расходились.
   */
  const forward = () => {
    history.pushState({ tolkStep: 2 }, '')
    setStep(2)
  }
  const back = () => {
    if ((history.state as { tolkStep?: number } | null)?.tolkStep === step) history.back()
    else setStep(1)
  }
  // После перезагрузки визард начинается с первого шага, а запись истории помнит прежний.
  useEffect(() => {
    if ((history.state as { tolkStep?: number } | null)?.tolkStep) history.replaceState(null, '')
  }, [])
  /*
   * Показанный шаг может отстать от сохранённого: комната пропала, пока человек
   * был на втором. Запись истории при этом остаётся вторым шагом, и «назад» с
   * честного первого повёл бы никуда. Лишнюю запись снимаем тем же `history.back()`:
   * `popstate` сам вернёт шаг, и цикл сойдётся.
   */
  useEffect(() => {
    if (step === view) return
    if ((history.state as { tolkStep?: number } | null)?.tolkStep === step) history.back()
    else setStep(view)
  }, [step, view])
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      setStep((e.state as { tolkStep?: number } | null)?.tolkStep === 2 ? 2 : 1)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  /*
   * Фокус идёт за шагом: на втором — на саму карточку, не на кнопку: «Join» под
   * рукой означала бы, что лишний Enter после выбора комнаты сразу входит. На
   * первом — к выбранной строке. Сравниваем с прошлым шагом, а не с «первым
   * рендером»: в StrictMode эффект запускается дважды, и при монтировании фокус
   * никуда уезжать не должен.
   */
  const prevView = useRef(view)
  useEffect(() => {
    if (prevView.current === view) return
    prevView.current = view
    const id =
      view === 2 ? 'join-room' : fresh !== null ? 'new-room-name' : pick ? `room-${pick}` : 'new-room-name'
    document.getElementById(id)?.focus({ preventScroll: true })
  }, [view, fresh, pick])

  const choose = (code: string) => {
    setMode('play')
    setFresh(null)
    setPick(code)
    setNotice(null)
    forward()
  }

  const createRoom = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const roomName = newRoomName.trim()
    if (!roomName) return
    setNotice(null)
    setMode('play')
    flushSync(() => {
      setPick(null)
      setFresh(roomName)
      setNewRoomName('')
    })
    forward()
  }

  const onName = (value: string) => {
    setName(value)
    setNameError(null)
    setDismissed(s.lastError)
  }

  // --- карточки --------------------------------------------------------------

  const roomsCard = (
    <section className={`w-full ${CARD} bg-surface/80`}>
      <h2 className={CARD_TITLE}>Select a room</h2>

      {notice && (
        <p
          role="status"
          className="mb-4 rounded-lg bg-amber-400/10 px-4 py-2.5 text-center text-xs text-amber-300"
        >
          {notice}
        </p>
      )}

      {/*
        Живой список: сервер шлёт его всем при каждом входе, выходе и создании,
        клиент только рисует. Клик по строке — выбор и показ стола.
      */}
      <ul className="space-y-1" aria-label="Rooms">
        {fresh !== null && (
          <li className="flex items-center gap-1 rounded-lg bg-white/10">
            {/* Строка ведёт дальше, как и остальные. */}
            <button
              type="button"
              aria-current="true"
              onClick={forward}
              className={`group min-w-0 flex-1 rounded-lg px-4 py-2.5 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 cursor-pointer`}
            >
              <span className="block truncate text-sm text-white">{fresh}</span>
              <span className="mt-0.5 block text-xs text-indigo-300">new room</span>
            </button>
            <button
              type="button"
              onClick={() => setFresh(null)}
              className="mr-2 shrink-0 cursor-pointer rounded-full px-2.5 py-1 text-xs text-gray-400 hover:bg-white/5 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
            >
              cancel
            </button>
          </li>
        )}
        {rooms.length === 0 && fresh === null && (
          <li className="px-4 py-6 text-center text-sm text-gray-500">No open rooms yet</li>
        )}
        {rooms.map((r) => {
          const on = fresh === null && pick === r.code
          return (
            <li key={r.code}>
              <button
                type="button"
                id={`room-${r.code}`}
                aria-current={on || undefined}
                onClick={() => choose(r.code)}
                className={`group flex w-full cursor-pointer items-center justify-between gap-4 rounded-lg px-4 py-2.5 text-left hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 ${on ? 'bg-white/10' : ''}`}
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-normal text-gray-200 group-hover:text-white">
                    {r.roomName}
                  </span>
                  <span
                    className={`mt-0.5 flex items-center gap-1.5 text-xs ${r.started ? 'text-sky-400' : 'text-amber-400'}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`size-1.5 rounded-full ${r.started ? 'bg-sky-400' : 'bg-amber-400'}`}
                    />
                    <span className="-translate-y-px">
                      {r.finished ? 'finished' : r.started ? 'playing' : 'waiting'}
                    </span>
                    {r.code === held && <span className="-translate-y-px text-indigo-300">· your table</span>}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2 text-right text-gray-400">
                  <span>
                    <span className="block text-sm tabular-nums">
                      {r.players}/{r.maxPlayers}
                    </span>
                    <span className="block text-[10px]">players</span>
                  </span>
                  <ChevronRightIcon
                    aria-hidden="true"
                    className="-mr-1 size-6 text-gray-400 group-hover:text-gray-200"
                  />
                </span>
              </button>
            </li>
          )
        })}

        {/*
          Новую комнату создают, пока их меньше предела (R118): при полном списке
          поле не нужно и спрятано, а с освободившимся местом возвращается само —
          список живой. Уже выбранная новая комната (`fresh`) поле не держит.
        */}
        {rooms.length < MAX_ROOMS && (
          <li className="mt-6 border-t border-white/10 pt-6">
            <form onSubmit={createRoom} className="flex min-w-0">
              <label htmlFor="new-room-name" className="sr-only">
                New room name
              </label>
              <div className="relative min-w-0 flex-1">
                <input
                  id="new-room-name"
                  maxLength={ROOM_NAME_MAX}
                  value={newRoomName}
                  onChange={(event) => setNewRoomName(event.target.value)}
                  placeholder="Your room name"
                  className={`${FIELD} pr-24`}
                />
                <button
                  type="submit"
                  tabIndex={-1}
                  onMouseDown={(event) => event.preventDefault()}
                  disabled={!newRoomName.trim()}
                  className="absolute inset-y-1.5 right-1.5 rounded-full bg-indigo-700 px-3 text-[11px] font-bold tracking-wide text-white uppercase hover:bg-indigo-600 disabled:cursor-default disabled:bg-white/10 disabled:text-gray-500"
                >
                  Create
                </button>
              </div>
            </form>
          </li>
        )}
      </ul>
    </section>
  )

  /**
   * Моё место за столом: в ряду, после чужих фишек, обычной фишкой в своём
   * цвете, без контура. Не кнопка и не
   * затемнена: это такая же фишка, как соседние, а править её — дело микро-карточки
   * рядом с переключателем роли. Ряд может сдвинуть моё место вправо, когда кто-то
   * вошёл; промах по нему ничего не делает.
   */
  const named = name.trim().length > 0

  /**
   * Микро-карточка игрока: аватарка в кольце цвета, имя и карандаш. Стоит в одной
   * строке с переключателем роли и открывает правку. Не прячется при «View»:
   * строка не должна менять состав и прыгать от смены роли, а имя и фишка нужны
   * и зрителю — он войдёт игроком, когда передумает.
   */
  const microCard = (
    <button
      type="button"
      onClick={openEditor}
      aria-label={named ? `Edit your player, ${name.trim()}` : 'Set up your player'}
      className="group flex h-9 min-w-0 grow basis-32 cursor-pointer items-center gap-2 rounded-full bg-white/5 pr-3.5 pl-1.5 outline-1 -outline-offset-1 outline-white/10 transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
    >
      <span
        className="size-6 shrink-0 overflow-hidden rounded-full bg-deep/60"
        style={{ boxShadow: `0 0 0 2px ${color}` }}
      >
        <svg viewBox="0 0 300 300" className="size-full" aria-hidden="true">
          <use href={`#avatar-0-${look.avatar}`} />
        </svg>
      </span>
      <span className="min-w-0 truncate text-sm font-medium text-white">
        {named ? name.trim() : <span className="text-gray-500">Your name</span>}
      </span>
      <PencilIcon aria-hidden="true" className="size-3.5 shrink-0 text-gray-500 group-hover:text-white" />
    </button>
  )

  const roomCard = (
    <section
      id="join-room"
      tabIndex={-1}
      className={`flex-1 ${CARD} bg-surface/80 lg:w-[23.625rem] lg:flex-none`}
    >
      <h2 className={CARD_TITLE}>Join the room</h2>

      {/* Какая это комната: название и статус из живого списка, тем же видом, что строка в первой карточке. */}
      <div className="text-center">
        {target && (
          <p className="flex items-center justify-center gap-3">
            <span className="truncate text-lg font-bold text-white">{target.roomName}</span>
            {picked ? (
              <span
                className={`flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${picked.started ? 'bg-sky-400/10 text-sky-400' : 'bg-amber-400/10 text-amber-400'}`}
              >
                <span
                  aria-hidden="true"
                  className={`size-1.5 rounded-full ${picked.started ? 'bg-sky-400' : 'bg-amber-400'}`}
                />
                <span className="-translate-y-px">
                  {picked.finished ? 'finished' : picked.started ? 'playing' : 'waiting'}
                </span>
              </span>
            ) : (
              <span className="shrink-0 rounded-full bg-indigo-300/10 px-2.5 py-1 text-xs font-medium text-indigo-300">
                new room
              </span>
            )}
          </p>
        )}
        {/*
          Сколько народу за столом — из живого списка: места и зрителей считает
          сервер, лобби только показывает. Без выбора считать нечего, и вместо
          нуля стоит подсказка в том же виде, что у остальных пустых состояний.
          Порог автозапуска показан, только когда он задан: менять его отсюда
          нечем, и «off» ничего игроку не говорит (R116).
        */}
        <p className={target ? 'mt-3 text-sm text-gray-400' : 'text-sm text-gray-500'}>
          {target ? (
            <>
              <span className="font-semibold text-gray-200 tabular-nums">{seats.length}</span>
              {seats.length === 1 ? ' player and ' : ' players and '}
              <span className="font-semibold text-gray-200 tabular-nums">{viewers}</span>
              {viewers === 1 ? ' viewer' : ' viewers'}
              {!picked?.started && (picked?.autoStartMin ?? 0) > 0 && (
                <span className="text-gray-500">{` · auto-start at ${picked?.autoStartMin}`}</span>
              )}
            </>
          ) : (
            'Choose a room'
          )}
        </p>
      </div>

      <hr className="my-6 border-white/10" />

      {/*
        Стол: чужие фишки, затем моя в роли игрока, затем свободные места
        пунктиром. Входят кнопкой ниже.
      */}
      <ul
        ref={seatsRef}
        className="flex flex-wrap content-center justify-center gap-x-1 gap-y-3"
        style={{ minHeight: seatHeight(seatW) }}
        aria-label="Players"
      >
        {!target && <li className="w-full px-4 text-center text-sm text-gray-500">No room selected</li>}
        {target && (
          <>
            {seats.map((seat, i) => (
              <li key={`${seat.name}-${i}`}>
                <SeatPlayer {...seat} seat={i} size={seatW} />
              </li>
            ))}
            {role === 'play' && !sitting && (
              <li key="me">
                <SeatPlayer {...look} name={name.trim() || 'YOU'} size={seatW} />
              </li>
            )}
            {Array.from(
              { length: Math.max(0, slots - seats.length - (role === 'play' && !sitting ? 1 : 0)) },
              (_, i) => (
                <li key={`free-${i}`}>
                  <FreeSeat size={seatW} />
                </li>
              ),
            )}
          </>
        )}
      </ul>

      {/*
        Роль и мой игрок в одной строке на всю ширину карточки, по тем же краям, что
        кнопки ниже: микро-карточка слева, переключатель справа (на узком экране
        карточка уходит строкой выше и тоже тянется). Переключатель меняет моё
        место в ряду сразу, а входит одна кнопка ниже, и её надпись идёт за ним.
      */}
      {!sitting && (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {microCard}
          <RoleSwitch role={role} canPlay={!locked} canWatch={canWatch} onChange={setMode} />
        </div>
      )}

      {lockNote && <p className="mt-4 text-center text-sm text-gray-400">{lockNote}</p>}
      {error && !editing && (
        <p role="alert" className="mt-4 text-center text-sm text-red-500">
          {error}
        </p>
      )}

      <div className="mt-6 flex gap-2 min-[26rem]:gap-3">
        <BackButton onClick={back} />
        <button
          type="button"
          id="join-button"
          disabled={!target || pending || (sitting && !mine)}
          aria-busy={pending}
          onClick={mine ? () => bridge.returnToGame() : role === 'watch' ? watchRoom : join}
          className={`${BUTTON} min-w-0 flex-1`}
        >
          {mine
            ? 'Return to game'
            : role === 'watch'
              ? 'View'
              : target?.code === null
                ? 'Create & play'
                : 'Play'}
        </button>
      </div>
      {/* Из лобби выйти из партии насовсем: освободить место и войти куда-то ещё. */}
      {mine && <LeaveGameButton offline={!s.connected} onLeave={() => bridge.leaveGame()} align="center" />}
    </section>
  )

  const lookForm = (
    <LookForm
      name={name}
      onName={onName}
      nameError={nameError ?? (s.lastErrorCode === 'name_taken' ? serverError : null)}
      pawn={look.pawn}
      avatar={look.avatar}
      takenPawns={look.takenPawns}
      takenAvatars={look.takenAvatars}
      onPawn={setPawn}
      onAvatar={setAvatar}
      nameRef={nameInput}
      onEnter={closeEditor}
    />
  )

  return (
    <div className="relative flex min-h-full px-6 py-12">
      {/* Связь пропала: действия, которым нужен сервер, молчат, и надо сказать почему. */}
      <ConnectionNote className="absolute top-4 left-1/2 -translate-x-1/2" />

      <div
        {...lockedWhen(!s.connected)}
        className={`m-auto w-full max-w-[23.625rem] ${MIN_CARD} ${dimWhen(!s.connected)}`}
      >
        {/* Ключ по шагу: новая карточка проявляется заново, а не подменяет прежнюю на месте. */}
        <div key={view} className="animate-fade-in motion-reduce:animate-none">
          {view === 1 ? roomsCard : roomCard}
        </div>
        {/* Пагинатор под формой: сначала содержимое шага, затем где мы. */}
        <Stepper step={view} total={2} />
      </div>

      {editing && (
        <Shell shift={shift} titleId="look-title" onDismiss={closeEditor} focusRef={nameInput}>
          <h2 id="look-title" className={CARD_TITLE}>
            Your player
          </h2>
          {lookForm}
          <button type="button" onClick={closeEditor} className={`${BUTTON} mt-8`}>
            Done
          </button>
        </Shell>
      )}
    </div>
  )
}

/** Где мы в визарде: точки над карточкой, текущая шире. Для диктора — одна фраза. */
function Stepper({ step, total }: { step: number; total: number }) {
  return (
    <div
      role="img"
      aria-label={`Step ${step} of ${total}`}
      className="mt-6 flex items-center justify-center gap-2"
    >
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`h-1.5 rounded-full ${i + 1 === step ? 'w-6 bg-indigo-400' : i + 1 < step ? 'w-1.5 bg-indigo-400/60' : 'w-1.5 bg-white/20'}`}
        />
      ))}
    </div>
  )
}
